import React, { useContext, useState } from "react";
import { Button } from "primereact/button";

import * as Yup from "yup";
import Flex from "../../../../components/shared-styles/flexi";
import Input from "../../../../components/form/input"; 
import { formatErrors, handleInput, handleSubmit } from "../../../../services/utils/processors";
import DatePicker from "../../../../components/form/DatePicker";
import CountryDropdown from "../../../../components/form/country-dropdown";
import { ToastContext } from "../../../../services/contexts/toast-context";
import styled from "styled-components";

// -- Styled Components for Redesign --
const CardContainer = styled.div`
  min-width: 50vw;
  background-color: var(--surface-card, #ffffff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    min-width: 100%;
    padding: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--surface-border, #f3f4f6);
  padding-bottom: 16px;
  
  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: var(--text-color, #374151);
    font-weight: 600;
  }
`;

const GridLine = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Education = ({ defaultData = [],loading=false, onSave, onBack }) => {

  const toastRef = useContext(ToastContext);
  const [educations, setEducations] = useState(Array.isArray(defaultData) ? formatDateFields(defaultData) : []);
  const [educationError, setEducationError] = useState({});

  const schema = Yup.array().of(
    Yup.object({
      name: Yup.string().required("Degree Name is required"),
      start_date: Yup.date().required("Start Date is required"),
      end_date: Yup.date().optional(),
      university: Yup.string().required("University Name is required"),
      degree: Yup.string().required("Degree is required"),
      field_of_study: Yup.string().required("Field of Study is required"),
      country: Yup.string().required("Country is required"),
    })
  );

  const handleInputChange = (value, index, field) => {
    const newEducations = [...educations];
    newEducations[index][field] = value;
    setEducations(newEducations);
  };

  function formatDateFields(items) {
    return items.map(item => ({
      ...item,
      start_date: item.start_date ? (item.start_date.length === 4 ? new Date(`${item.start_date}-01-01`) : new Date(item.start_date)) : null,
      end_date: item.end_date ? (item.end_date.length === 4 ? new Date(`${item.end_date}-12-31`) : new Date(item.end_date)) : null,
    }));
  }

  const addEducation = () => {
    setEducations([
      ...educations,
      {
        name: "",
        start_date: "",
        end_date: "",
        university: "",
        degree: "",
        field_of_study: "",
        country: "",
      },
    ]);
  };

  const removeEducation = (index) => {
    const newEducations = educations.filter((_, i) => i !== index);
    setEducations(newEducations);
  };

  const handleSubmitData = () => {
    handleSubmit(educations, schema, setEducationError, onSave);
    if (educationError && Object.entries(educationError).length != 0) {
      toastRef.current.show({ severity: 'error', summary: "Error", detail: formatErrors(educationError), life: 6000 });
    }
    setEducationError({})
  };

  return (
    <div style={{ padding: "20px 0" }}>
      {/* Add Education Button */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
        <Button
          label="Add Education"
          icon="pi pi-plus"
          outlined
          className="p-button-secondary"
          onClick={addEducation}
        />
      </div>

      {/* Educations List */}
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
        {educations.map((education, index) => (
          <CardContainer key={index}>
            <CardHeader>
              <h3>{education.name ? education.name : `Education ${index + 1}`}</h3>
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-danger p-button-text"
                onClick={() => removeEducation(index)}
                aria-label="Remove Education"
                style={{ padding: "8px" }}
              />
            </CardHeader>
            <Flex direction="column" align="stretch" gap="20px" style={{width:"100%"}}>
              
              <GridLine>
                <Input
                  value={education.name}
                  label="Program / Certificate Name"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "name")}
                  error={educationError?.[`${index}`]?.name}
                />
                <Input
                  value={education.university}
                  label="University / Institution"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "university")}
                  error={educationError?.[`${index}`]?.university}
                />
              </GridLine>

              <GridLine>
                <DatePicker
                  value={education.start_date}
                  label="Start Date"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "start_date")}
                  error={educationError?.[`${index}`]?.start_date}
                />
                <DatePicker
                  value={education.end_date}
                  label="End Date"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "end_date")}
                  error={educationError?.[`${index}`]?.end_date}
                />
              </GridLine>

              <GridLine>
                <Input
                  value={education.degree}
                  label="Degree (e.g. Master, Bachelor)"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "degree")}
                  error={educationError?.[`${index}`]?.degree}
                />
                <Input
                  value={education.field_of_study}
                  label="Field of Study"
                  width="100%"
                  onChange={(value) => handleInputChange(value, index, "field_of_study")}
                  error={educationError?.[`${index}`]?.field_of_study}
                />
              </GridLine>

              <GridLine>
                <CountryDropdown
                  value={education.country || ""}
                  error={educationError?.[`${index}`]?.country}
                  onChange={(value) => handleInputChange(value, index, "country")}
                />
              </GridLine>

            </Flex>
          </CardContainer>
        ))}
      </div>
      
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "24px" }}>
        <Button 
          label="Save & Continue" 
          icon="pi pi-check" 
          loading={loading} 
          disabled={loading} 
          className="p-button-primary" 
          onClick={handleSubmitData} 
        />
      </div>
    </div>
  );
};

export default Education;
